import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Link,
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import Intro from "../../../components/atoms/Intro/Intro.component";
import { apiConstants } from "../../../constants";
import { authHeader, getUserToken } from "../../../helpers";

const ShopifyAppList = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch single organization if needed


  // Local state
  const [shopifyStores, setShopifyStores] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const [formState, setFormState] = useState({
    name: "",
    url: "",
    access_token: "",
    api_key: "",
    secret_key: "",
    shopify_store_name: "",
    image: "",
  });

  // Load stores
  useEffect(() => {
    fetchShopifyStores();
  }, [id]);

  const fetchShopifyStores = () => {
    fetch(`${apiConstants.API_URL}/shopify_apps`, {
      method: "GET",
      headers: authHeader(),
    })
      .then((res) => res.json())
      .then((data) => setShopifyStores(data?.data || []));
  };

  // Handlers
  const handleOpenAddModal = () => {
    setCurrentStore(null);
    setFormState({
      name: "",
      url: "",
      access_token: "",
      api_key: "",
      secret_key: "",
      shopify_store_name: "",
      image: "",
    });
    setOpenModal(true);
  };

  const handleEditStore = (store) => {
    setCurrentStore(store);
    setFormState({
      name: store.name,
      url: store.url,
      access_token: store.access_token,
      api_key: store.api_key,
      secret_key: store.secret_key,
      shopify_store_name: store.shopify_store_name,
      image: store.image || "",
      image_url: store.image_url || "",
    });
    setOpenModal(true);
  };

  const handleDeleteStore = (store) => {
    if (window.confirm("Are you sure you want to delete this store?")) {
      fetch(`${apiConstants.API_URL}/shopify_apps/delete/${store.id}`, {
        method: "DELETE",
        headers: authHeader(),
      })
        .then((res) => res.json())
        .then(fetchShopifyStores);
    }
  };

  const handleActivateStore = (store) => {
    fetch(`${apiConstants.API_URL}/shopify_apps/active/${store.id}`, {
      method: "GET", // adjust if your API uses PUT
      headers: authHeader(),
    })
      .then((res) => res.json())
      .then(fetchShopifyStores);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentStore(null);
  };

  const handleSubmitForm = () => {
    const url = currentStore
      ? `${apiConstants.API_URL}/shopify_apps/update/${currentStore.id}`
      : `${apiConstants.API_URL}/shopify_apps/create`;
    const method = currentStore ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body: JSON.stringify(formState),
    })
      .then((res) => res.json())
      .then(() => {
        fetchShopifyStores();
        handleCloseModal();
      });
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);
    fetch(`${apiConstants.API_URL}/images/shopify`, {
      method: "POST",
      headers: { Authorization: "Bearer " + getUserToken() },
      body: fd,
    })
      .then((res) => res.json())
      .then((data) =>
        setFormState((prev) => ({
          ...prev,
          image: data.data.path,
          image_url: data.data.url,
        }))
      );
  };

  // Breadcrumbs
  const breadcrumbs = [
    <Link key="1" underline="hover" color="inherit" href="/admin/shopify-management/">
      Shopify Management
    </Link>,
    <Typography key="2">Apps</Typography>,
  ];

  return (
    <>
      <Intro
        pageTitle="Apps"
        pageTitleShort="Store"
        breadcrumbs={breadcrumbs}
        additionalWidgets=""
      />

      <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Store
        </Button>
      </Stack>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {shopifyStores.map((store) => (
            <Grid item xs={12} sm={6} md={4} key={store.id}>
              <Card
                sx={{
                  maxWidth: 345,
                  boxShadow: 3,
                  borderRadius: 2,
                  border: store.is_active ? "2px solid #4caf50" : "none",
                }}
              >
                <CardMedia
                  component="img"
                  height="180"
                  image={store.image_url || "https://via.placeholder.com/300x180?text=No+Image"}
                  alt={store.name}
                />
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{store.name}</Typography>
                    {store.is_active &&
                      <Chip label="Active" color="success" size="small" /> 
                    }
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    URL: {store.url}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access Token: {store.access_token}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    API Key: {store.api_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Secret Key: {store.secret_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shopify Store Name: {store.shopify_store_name}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button onClick={() => handleEditStore(store)}>Edit</Button>
                  <Button onClick={() => handleDeleteStore(store)}>Delete</Button>
                  <Button
                    variant={store.is_active ? "contained" : "outlined"}
                    color={store.is_active ? "success" : "primary"}
                    disabled={store.is_active}
                    onClick={() => handleActivateStore(store)}
                  >
                    Active
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{currentStore ? "Edit Store" : "Add New Store"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={formState.name}
            onChange={(e) => setFormState({ ...formState, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            value={formState.url}
            onChange={(e) => setFormState({ ...formState, url: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Access Token"
            fullWidth
            value={formState.access_token}
            onChange={(e) => setFormState({ ...formState, access_token: e.target.value })}
          />
          <TextField
            margin="dense"
            label="API Key"
            fullWidth
            value={formState.api_key}
            onChange={(e) => setFormState({ ...formState, api_key: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Secret Key"
            fullWidth
            value={formState.secret_key}
            onChange={(e) => setFormState({ ...formState, secret_key: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Shopify Store Name"
            fullWidth
            value={formState.shopify_store_name}
            onChange={(e) => setFormState({ ...formState, shopify_store_name: e.target.value })}
          />
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" component="label">
              Upload Image
              <input type="file" accept="image/*" hidden onChange={onFileChange} />
            </Button>
            {formState.image_url && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <img
                  src={formState.image_url}
                  alt="Store"
                  style={{ maxWidth: "100%", maxHeight: 200 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSubmitForm}>
            {currentStore ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShopifyAppList;
